import { useMemo, useState } from 'react'
import {
  App, Alert, Button, Card, Form, Input, InputNumber, Modal, Select,
  Space, Tabs, Tag, Typography,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ReloadOutlined, ImportOutlined } from '@ant-design/icons'

import { sparesApi, workOrdersApi } from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'
import { usePlantId } from '@/shared/hooks/usePlantId'
import type { SparePart } from '@/shared/types/maintenance'

const { Text } = Typography

/**
 * Spec §8/§9 — Store Officer's spare desk.
 * Tabs: Stock View · Issue against WO · Bulk import.
 *
 * The Issue form pulls all metadata (rate, available stock, UOM, machine,
 * department) from the linked work_order_id + spare_id once selected.
 * Store never types rate or value — backend computes value = qty × rate.
 */
export default function SpareIssuePage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const { plantId } = usePlantId()
  const canIssue = usePermission('maintenance.update')

  const [issueOpen, setIssueOpen] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [filters, setFilters] = useState<{ critical?: boolean; lowStock?: boolean }>({})

  // ── Spare list ────────────────────────────────────────────────────────
  const { data: spares, isLoading } = useQuery({
    queryKey: ['spares-list', filters],
    queryFn: () => sparesApi.list({
      critical_only: filters.critical, low_stock_only: filters.lowStock, limit: 1000,
    }),
  })

  // WO list — only spare-relevant statuses
  const { data: openWOs } = useQuery({
    queryKey: ['open-work-orders', plantId],
    enabled: !!plantId,
    queryFn: () => workOrdersApi.list({ plant_id: plantId, only_open: true, limit: 500 }),
  })

  const lowStock = useMemo(
    () => (spares ?? []).filter((s) => Number(s.current_stock) <= Number(s.reorder_level)),
    [spares],
  )

  // ── Issue mutation ────────────────────────────────────────────────────
  const [issueForm] = Form.useForm()
  const issueMutation = useMutation({
    mutationFn: (vals: {
      work_order_id: string; spare_id: string; issued_qty: number; remarks?: string;
    }) => sparesApi.issue(vals),
    onSuccess: (txn) => {
      message.success(`Issued ${txn.transaction_no} — ₹ ${txn.value} posted to WO`)
      setIssueOpen(false)
      qc.invalidateQueries({ queryKey: ['spares-list'] })
      qc.invalidateQueries({ queryKey: ['work-orders'] })
    },
    onError: (e) => message.error((e as { message?: string })?.message ?? 'Issue failed'),
  })

  // ── Bulk import ───────────────────────────────────────────────────────
  const [csvText, setCsvText] = useState('')
  const bulkMutation = useMutation({
    mutationFn: () => {
      const lines = csvText.trim().split('\n')
      const [header, ...body] = lines
      const cols = header.split(',').map((c) => c.trim())
      const rows = body.map((row) => {
        const cells = row.split(',')
        return Object.fromEntries(cols.map((c, i) => [c, cells[i]?.trim() ?? '']))
      })
      return sparesApi.bulkImport(rows)
    },
    onSuccess: (r) => {
      message.success(`Import: ${r.created} created, ${r.updated} updated, ${r.errors.length} errors`)
      qc.invalidateQueries({ queryKey: ['spares-list'] })
      setBulkOpen(false)
      setCsvText('')
    },
    onError: () => message.error('Import failed'),
  })

  // ── Auto-fetch when spare selected in issue form ──────────────────────
  const watchSpareId = Form.useWatch('spare_id', issueForm)
  const watchWoId = Form.useWatch('work_order_id', issueForm)
  const selectedSpare: SparePart | undefined =
    spares?.find((s) => s.id === watchSpareId)
  const selectedWO = openWOs?.find((w) => w.id === watchWoId)

  // ── Stock columns ─────────────────────────────────────────────────────
  const stockColumns = [
    { title: 'Code', dataIndex: 'spare_code', key: 'spare_code', width: 130 },
    { title: 'Name', dataIndex: 'spare_name', key: 'spare_name' },
    { title: 'Category', dataIndex: 'category', key: 'category', width: 120 },
    { title: 'UOM', dataIndex: 'uom', key: 'uom', width: 80 },
    {
      title: 'Stock',
      key: 'stock', width: 130,
      render: (_: unknown, r: SparePart) => {
        const stock = Number(r.current_stock)
        const reorder = Number(r.reorder_level)
        const low = stock <= reorder
        return (
          <Space>
            <Text strong style={{ color: low ? '#cf1322' : undefined }}>
              {stock} {r.uom}
            </Text>
            {low && <Tag color="red">REORDER</Tag>}
            {r.critical_spare && <Tag color="orange">CRITICAL</Tag>}
          </Space>
        )
      },
    },
    {
      title: 'Reorder lvl', dataIndex: 'reorder_level', key: 'reorder', width: 100,
      render: (v: string | number) => Number(v),
    },
    {
      title: 'Avg rate', dataIndex: 'average_rate', key: 'rate', width: 110,
      render: (v: string | number) => `₹ ${Number(v).toFixed(2)}`,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Spare Desk"
        subtitle="Stock • Issue against WO • Returns"
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => qc.invalidateQueries({ queryKey: ['spares-list'] })}>Refresh</Button>
            {canIssue && (
              <Button type="primary" onClick={() => { issueForm.resetFields(); setIssueOpen(true) }}>
                Issue against WO
              </Button>
            )}
            {canIssue && (
              <Button icon={<ImportOutlined />} onClick={() => setBulkOpen(true)}>Bulk import</Button>
            )}
          </Space>
        )}
      />

      {lowStock.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${lowStock.length} spare(s) at or below reorder level — review and raise PR`}
        />
      )}

      <Tabs
        items={[
          {
            key: 'stock',
            label: 'Stock view',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 12 }}>
                  <Space>
                    <Button
                      type={filters.critical ? 'primary' : 'default'}
                      onClick={() => setFilters((f) => ({ ...f, critical: !f.critical }))}>
                      Critical only
                    </Button>
                    <Button
                      type={filters.lowStock ? 'primary' : 'default'}
                      onClick={() => setFilters((f) => ({ ...f, lowStock: !f.lowStock }))}>
                      Low stock only
                    </Button>
                  </Space>
                </Card>
                <DataTable rowKey="id" columns={stockColumns} dataSource={spares} loading={isLoading} />
              </>
            ),
          },
        ]}
      />

      {/* Issue against WO */}
      <Modal
        title="Issue spare against work order"
        open={issueOpen}
        onOk={() => issueForm.validateFields().then((v) => issueMutation.mutate(v))}
        onCancel={() => setIssueOpen(false)}
        confirmLoading={issueMutation.isPending}
        width={600}
      >
        <Form form={issueForm} layout="vertical">
          <Form.Item name="work_order_id" label="Work order" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Select an open WO"
              options={(openWOs ?? []).map((w) => ({
                label: `${w.work_order_no} — ${w.priority} ${w.status}`,
                value: w.id,
              }))}
              filterOption={(i, o) => (o?.label as string).toLowerCase().includes(i.toLowerCase())}
            />
          </Form.Item>

          {selectedWO && (
            <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
              <Space split=" • ">
                <Text type="secondary">WO {selectedWO.work_order_no}</Text>
                <Text type="secondary">Priority {selectedWO.priority}</Text>
                <Text type="secondary">Status {selectedWO.status}</Text>
              </Space>
            </Card>
          )}

          <Form.Item name="spare_id" label="Spare item" rules={[{ required: true }]}>
            <Select
              showSearch
              placeholder="Search by code or name"
              options={(spares ?? []).map((s) => ({
                label: `${s.spare_code} — ${s.spare_name}`,
                value: s.id,
              }))}
              filterOption={(i, o) => (o?.label as string).toLowerCase().includes(i.toLowerCase())}
            />
          </Form.Item>

          {selectedSpare && (
            <Card size="small" style={{ marginBottom: 12, background: '#fafafa' }}>
              <Space split=" • ">
                <span>Stock: <Text strong>{Number(selectedSpare.current_stock)} {selectedSpare.uom}</Text></span>
                <span>Reorder: {Number(selectedSpare.reorder_level)}</span>
                <span>Rate: <Text strong>₹ {Number(selectedSpare.average_rate).toFixed(2)}</Text> <Tag color="geekblue">SYSTEM</Tag></span>
              </Space>
            </Card>
          )}

          <Form.Item
            name="issued_qty" label="Issue quantity"
            rules={[
              { required: true, type: 'number', min: 0.001, message: 'Enter a positive quantity' },
              {
                validator: (_, v) => (
                  selectedSpare && v != null && Number(v) > Number(selectedSpare.current_stock)
                    ? Promise.reject(new Error('Insufficient stock'))
                    : Promise.resolve()
                ),
              },
            ]}>
            <InputNumber min={0} step={0.5} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="remarks" label="Remarks">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Value (₹), stock decrement, WO cost rollup and reorder alert are all done by the system.
          </Text>
        </Form>
      </Modal>

      {/* Bulk import */}
      <Modal
        title="Bulk import spares"
        open={bulkOpen}
        onOk={() => bulkMutation.mutate()}
        onCancel={() => setBulkOpen(false)}
        confirmLoading={bulkMutation.isPending}
        width={640}
      >
        <Text>
          Paste a CSV with header: <code>spare_code,spare_name,uom,average_rate,current_stock</code>
        </Text>
        <Input.TextArea
          rows={10}
          style={{ marginTop: 8, fontFamily: 'monospace' }}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder={'spare_code,spare_name,uom,average_rate,current_stock\nSP-001,Bearing 6204,NOS,150,25'}
        />
      </Modal>
    </div>
  )
}
